import { Language } from '../types';

export const sasMigrationsTranslations: Record<Language, Record<string, string>> = {
  en: {
    // SAS Migrations Page
    modernize: "SAS to Python Migrations",
    sustainablePath: "A Sustainable Path to Modern Analytics",
    modernizeDesc: "Transform your legacy SAS analytics infrastructure into flexible, cost-effective Python-based solutions.",
    
    migrationContext: "Why Modernize Your Analytics Stack?",
    contextDesc: "Organizations with established SAS environments face increasing pressure to modernize their analytics infrastructure.",
    
    driversMigration: "Key drivers for migration:",
    
    costReduction: "Cost reduction and licensing flexibility",
    talentAvailability: "Access to a larger talent pool of Python developers",
    openSource: "Leverage the innovation of the open-source ecosystem",
    cloudIntegration: "Better integration with modern cloud platforms",
    agileMethodologies: "Alignment with agile development methodologies",
    
    migrationApproachSection: "Our Migration Approach",
    migrateSuccessText: "Successfully migrating complex analytics environments",
    
    codeAssessment: "Code Assessment",
    codeDesc: "We analyze your SAS codebase to understand complexity, dependencies, and migration challenges.",
    
    knowledgeTransfer: "Knowledge Transfer",
    transferDesc: "We document business logic and analytical processes embedded in your SAS code.",
    
    migrationStrategy: "Migration Strategy",
    strategyDesc: "We develop a phased approach to migration that minimizes disruption to ongoing operations.",
    
    pythonImplementation: "Python Implementation",
    pythonDesc: "We reimplement SAS functionality in Python, ensuring identical analytical results.",
    
    validationTesting: "Validation & Testing",
    testingDesc: "We rigorously test the new solutions against original SAS outputs to ensure accuracy.",
    
    deploymentIntegration: "Deployment & Integration",
    deploymentDesc: "We integrate the new Python solutions into your production environment.",
    
    migrationAdvantagesSection: "Key Advantages",
    pythonBenefitsText: "The benefits of Python-based analytics",
    
    costEfficiency: "Cost Efficiency",
    efficiencyDesc: "Significant reduction in licensing costs and infrastructure requirements.",
    
    flexibility: "Development Flexibility",
    flexibilityDesc: "Faster development cycles and easier integration with modern tools and platforms.",
    
    talentPool: "Expanded Talent Pool",
    poolDesc: "Access to a larger community of developers and data scientists.",
    
    modernTools: "Modern Tools & Methods",
    toolsDesc: "Leverage the latest advances in data science, machine learning, and AI.",
    
    futureProof: "Future-Proofing",
    futureDesc: "Ensure your analytics capabilities continue to evolve with technology trends.",
    
    successStoriesSection: "Success Stories",
    transformationsText: "Real-world transformations",
    
    healthcareTitle: "Healthcare Provider",
    healthcareDesc: "Migration of clinical analytics from SAS to Python reduced licensing costs by 72% while improving processing speed.",
    
    financeTitle: "Financial Services",
    financeDesc: "Bank transformed credit risk models from SAS to Python, increasing model development speed by 3x.",
    
    retailTitle: "Retail Chain",
    retailDesc: "Customer analytics platform migration enabled real-time personalization capabilities previously impossible with SAS.",
    
    readyStart: "Ready to Start Your Migration?",
    assessmentOffer: "Let's begin with a comprehensive assessment of your current SAS environment.",
    scheduleAssessment: "Schedule an Assessment",
    learnProcess: "Learn About Our Process",
  },
  de: {
    // SAS Migrations Page
    modernize: "SAS zu Python Migrationen",
    sustainablePath: "Ein nachhaltiger Weg zu moderner Analytik",
    modernizeDesc: "Transformieren Sie Ihre Legacy-SAS-Analyseinfrastruktur in flexible, kosteneffektive Python-basierte Lösungen.",
    
    migrationContext: "Warum Ihre Analytik-Stack modernisieren?",
    contextDesc: "Organisationen mit etablierten SAS-Umgebungen stehen unter zunehmendem Druck, ihre Analyseinfrastruktur zu modernisieren.",
    
    driversMigration: "Schlüsseltreiber für die Migration:",
    
    costReduction: "Kostenreduzierung und Lizenzflexibilität",
    talentAvailability: "Zugang zu einem größeren Talentpool von Python-Entwicklern",
    openSource: "Nutzen Sie die Innovation des Open-Source-Ökosystems",
    cloudIntegration: "Bessere Integration mit modernen Cloud-Plattformen",
    agileMethodologies: "Ausrichtung an agilen Entwicklungsmethoden",
    
    migrationApproachSection: "Unser Migrationsansatz",
    migrateSuccessText: "Erfolgreiche Migration komplexer Analyseumgebungen",
    
    codeAssessment: "Code-Bewertung",
    codeDesc: "Wir analysieren Ihren SAS-Codebase, um Komplexität, Abhängigkeiten und Migrationsherausforderungen zu verstehen.",
    
    knowledgeTransfer: "Wissenstransfer",
    transferDesc: "Wir dokumentieren Geschäftslogik und analytische Prozesse, die in Ihrem SAS-Code eingebettet sind.",
    
    migrationStrategy: "Migrationsstrategie",
    strategyDesc: "Wir entwickeln einen phasenweisen Ansatz zur Migration, der Störungen des laufenden Betriebs minimiert.",
    
    pythonImplementation: "Python-Implementierung",
    pythonDesc: "Wir reimplementieren SAS-Funktionalität in Python und gewährleisten identische analytische Ergebnisse.",
    
    validationTesting: "Validierung & Tests",
    testingDesc: "Wir testen die neuen Lösungen rigoros gegen ursprüngliche SAS-Outputs, um Genauigkeit zu gewährleisten.",
    
    deploymentIntegration: "Bereitstellung & Integration",
    deploymentDesc: "Wir integrieren die neuen Python-Lösungen in Ihre Produktionsumgebung.",
    
    migrationAdvantagesSection: "Hauptvorteile",
    pythonBenefitsText: "Die Vorteile von Python-basierter Analytik",
    
    costEfficiency: "Kosteneffizienz",
    efficiencyDesc: "Erhebliche Reduzierung der Lizenzkosten und Infrastrukturanforderungen.",
    
    flexibility: "Entwicklungsflexibilität",
    flexibilityDesc: "Schnellere Entwicklungszyklen und einfachere Integration mit modernen Tools und Plattformen.",
    
    talentPool: "Erweiterter Talentpool",
    poolDesc: "Zugang zu einer größeren Gemeinschaft von Entwicklern und Datenwissenschaftlern.",
    
    modernTools: "Moderne Tools & Methoden",
    toolsDesc: "Nutzen Sie die neuesten Fortschritte in Datenwissenschaft, maschinellem Lernen und KI.",
    
    futureProof: "Zukunftssicherheit",
    futureDesc: "Stellen Sie sicher, dass Ihre Analysefähigkeiten weiterhin mit Technologietrends evolvieren.",
    
    successStoriesSection: "Erfolgsgeschichten",
    transformationsText: "Reale Transformationen",
    
    healthcareTitle: "Gesundheitsdienstleister",
    healthcareDesc: "Migration klinischer Analysen von SAS zu Python reduzierte Lizenzkosten um 72% bei gleichzeitiger Verbesserung der Verarbeitungsgeschwindigkeit.",
    
    financeTitle: "Finanzdienstleistungen",
    financeDesc: "Bank transformierte Kreditrisikomodelle von SAS zu Python und erhöhte die Modellentwicklungsgeschwindigkeit um das 3-fache.",
    
    retailTitle: "Einzelhandelskette",
    retailDesc: "Migration der Kundenanalyseplattform ermöglichte Echtzeit-Personalisierungsfähigkeiten, die zuvor mit SAS unmöglich waren.",
    
    readyStart: "Bereit, Ihre Migration zu starten?",
    assessmentOffer: "Beginnen wir mit einer umfassenden Bewertung Ihrer aktuellen SAS-Umgebung.",
    scheduleAssessment: "Bewertung planen",
    learnProcess: "Erfahren Sie mehr über unseren Prozess",
  },
  sk: {
    // SAS Migrations Page - using English as fallback for now
    modernize: "Migrácie zo SAS do Python",
    sustainablePath: "Udržateľná cesta k modernej analytike",
    modernizeDesc: "Transform your legacy SAS analytics infrastructure into flexible, cost-effective Python-based solutions.",
    // Using English fallbacks for all other keys
    migrationContext: "Why Modernize Your Analytics Stack?",
    contextDesc: "Organizations with established SAS environments face increasing pressure to modernize their analytics infrastructure.",
    driversMigration: "Key drivers for migration:",
    costReduction: "Cost reduction and licensing flexibility",
    talentAvailability: "Access to a larger talent pool of Python developers",
    openSource: "Leverage the innovation of the open-source ecosystem",
    cloudIntegration: "Better integration with modern cloud platforms",
    agileMethodologies: "Alignment with agile development methodologies",
    migrationApproachSection: "Our Migration Approach",
    migrateSuccessText: "Successfully migrating complex analytics environments",
    codeAssessment: "Code Assessment",
    codeDesc: "We analyze your SAS codebase to understand complexity, dependencies, and migration challenges.",
    knowledgeTransfer: "Knowledge Transfer",
    transferDesc: "We document business logic and analytical processes embedded in your SAS code.",
    migrationStrategy: "Migration Strategy",
    strategyDesc: "We develop a phased approach to migration that minimizes disruption to ongoing operations.",
    pythonImplementation: "Python Implementation",
    pythonDesc: "We reimplement SAS functionality in Python, ensuring identical analytical results.",
    validationTesting: "Validation & Testing",
    testingDesc: "We rigorously test the new solutions against original SAS outputs to ensure accuracy.",
    deploymentIntegration: "Deployment & Integration",
    deploymentDesc: "We integrate the new Python solutions into your production environment.",
    migrationAdvantagesSection: "Key Advantages",
    pythonBenefitsText: "The benefits of Python-based analytics",
    costEfficiency: "Cost Efficiency",
    efficiencyDesc: "Significant reduction in licensing costs and infrastructure requirements.",
    flexibility: "Development Flexibility",
    flexibilityDesc: "Faster development cycles and easier integration with modern tools and platforms.",
    talentPool: "Expanded Talent Pool",
    poolDesc: "Access to a larger community of developers and data scientists.",
    modernTools: "Modern Tools & Methods",
    toolsDesc: "Leverage the latest advances in data science, machine learning, and AI.",
    futureProof: "Future-Proofing",
    futureDesc: "Ensure your analytics capabilities continue to evolve with technology trends.",
    successStoriesSection: "Success Stories",
    transformationsText: "Real-world transformations",
    healthcareTitle: "Healthcare Provider",
    healthcareDesc: "Migration of clinical analytics from SAS to Python reduced licensing costs by 72% while improving processing speed.",
    financeTitle: "Financial Services",
    financeDesc: "Bank transformed credit risk models from SAS to Python, increasing model development speed by 3x.",
    retailTitle: "Retail Chain",
    retailDesc: "Customer analytics platform migration enabled real-time personalization capabilities previously impossible with SAS.",
    readyStart: "Ready to Start Your Migration?",
    assessmentOffer: "Let's begin with a comprehensive assessment of your current SAS environment.",
    scheduleAssessment: "Schedule an Assessment",
    learnProcess: "Learn About Our Process",
  },
  cs: {
    // SAS Migrations Page
    modernize: "Migrace ze SAS do Pythonu",
    sustainablePath: "Udržitelná cesta k moderní analýze",
    modernizeDesc: "Transformujte svou zastaralou analytickou infrastrukturu SAS na flexibilní, nákladově efektivní řešení založená na Pythonu.",
    
    migrationContext: "Proč modernizovat svou analytickou infrastrukturu?",
    contextDesc: "Organizace se zavedenými prostředími SAS čelí stále většímu tlaku na modernizaci své analytické infrastruktury.",
    
    driversMigration: "Klíčové důvody pro migraci:",
    
    costReduction: "Snížení nákladů a flexibilita licencování",
    talentAvailability: "Přístup k většímu fondu talentů Python vývojářů",
    openSource: "Využití inovací open-source ekosystému",
    cloudIntegration: "Lepší integrace s moderními cloudovými platformami",
    agileMethodologies: "Soulad s agilními metodikami vývoje",
    
    migrationApproachSection: "Náš přístup k migraci",
    migrateSuccessText: "Úspěšná migrace složitých analytických prostředí",
    
    codeAssessment: "Hodnocení kódu",
    codeDesc: "Analyzujeme váš kódový základ SAS, abychom porozuměli složitosti, závislostem a výzvám migrace.",
    
    knowledgeTransfer: "Přenos znalostí",
    transferDesc: "Dokumentujeme obchodní logiku a analytické procesy vložené ve vašem kódu SAS.",
    
    migrationStrategy: "Migrační strategie",
    strategyDesc: "Vyvíjíme fázovaný přístup k migraci, který minimalizuje narušení probíhajících operací.",
    
    pythonImplementation: "Implementace Pythonu",
    pythonDesc: "Přeimplementujeme funkcionalitu SAS v Pythonu a zajišťujeme identické analytické výsledky.",
    
    validationTesting: "Validace a testování",
    testingDesc: "Přísně testujeme nová řešení proti původním výstupům SAS, abychom zajistili přesnost.",
    
    deploymentIntegration: "Nasazení a integrace",
    deploymentDesc: "Integrujeme nová řešení Pythonu do vašeho produkčního prostředí.",
    
    migrationAdvantagesSection: "Klíčové výhody",
    pythonBenefitsText: "Výhody analytiky založené na Pythonu",
    
    costEfficiency: "Nákladová efektivita",
    efficiencyDesc: "Významné snížení licenčních nákladů a požadavků na infrastrukturu.",
    
    flexibility: "Flexibilita vývoje",
    flexibilityDesc: "Rychlejší vývojové cykly a snadnější integrace s moderními nástroji a platformami.",
    
    talentPool: "Rozšířený fond talentů",
    poolDesc: "Přístup k větší komunitě vývojářů a datových vědců.",
    
    modernTools: "Moderní nástroje a metody",
    toolsDesc: "Využijte nejnovější pokroky v datové vědě, strojovém učení a AI.",
    
    futureProof: "Budoucí připravenost",
    futureDesc: "Zajistěte, aby vaše analytické schopnosti i nadále evolvovaly s technologickými trendy.",
    
    successStoriesSection: "Příběhy úspěchu",
    transformationsText: "Skutečné transformace",
    
    healthcareTitle: "Poskytovatel zdravotní péče",
    healthcareDesc: "Migrace klinické analýzy ze SAS do Pythonu snížila licenční náklady o 72 % při současném zlepšení rychlosti zpracování.",
    
    financeTitle: "Finanční služby",
    financeDesc: "Banka transformovala modely kreditního rizika ze SAS do Pythonu a zvýšila rychlost vývoje modelů 3x.",
    
    retailTitle: "Retailový řetězec",
    retailDesc: "Migrace platformy zákaznické analýzy umožnila možnosti personalizace v reálném čase, které byly dříve se SAS nemožné.",
    
    readyStart: "Připraveni začít svou migraci?",
    assessmentOffer: "Začněme komplexním hodnocením vašeho současného prostředí SAS.",
    scheduleAssessment: "Naplánovat hodnocení",
    learnProcess: "Zjistit více o našem procesu",
  }
};
